/**
 * WeeklyStreakStrip — the home-hero Monday-first week strip.
 *
 * iOS source: WeeklyCalendar.tsx lines 229-267 — 7 columns space-around, each
 * with a single-letter label (M T W T F S S) above a 34px day circle. Today is
 * a filled white circle with a blue number; past streak days show a white
 * check; missed days are dashed/faint; future days are faint.
 *
 * Rendered on the blue sky hero (surface="hero").
 */
import { FC } from "react";
import StreakDayCircle from "./StreakDayCircle";
import { computeHomeWeek } from "./streakUtils";
import type { DayActivity } from "./streakUtils";
import { useStreakCalendar } from "./StreakCalendarContext";

export interface WeeklyStreakStripProps {
  dayStreak: number;
  /** Per-day logged listening, so days with activity outside the consecutive
   *  streak window still show completed (iOS computeWeekDays parity). */
  weeklyData?: DayActivity[];
}

const WeeklyStreakStrip: FC<WeeklyStreakStripProps> = ({
  dayStreak,
  weeklyData,
}) => {
  const week = computeHomeWeek(dayStreak, new Date(), weeklyData);
  // Tapping the strip opens the month-streak overlay (iOS WeeklyCalendar.tsx:228
  // — the strip Pressable sets calendarExpanded), it does NOT navigate.
  const { open: openStreakCalendar } = useStreakCalendar();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openStreakCalendar}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openStreakCalendar();
        }
      }}
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        width: "100%",
        maxWidth: 360,
        margin: "0 auto",
        gap: 2,
        cursor: "pointer",
      }}
    >
      {week.map((d, i) => (
        <StreakDayCircle
          key={i}
          label={d.label}
          dayNumber={d.dayNumber}
          state={d.state}
          surface="hero"
          showNumber
        />
      ))}
    </div>
  );
};

export default WeeklyStreakStrip;
